import { Container } from 'components/box';
import { PageProps, QueryClearanceProductsOutput } from 'types/types';
import clearanceProductsQuery from 'queries/clearanceProducts.graphql';
import QueryError from 'components/query-error';
import { setGTMPageName } from '../util/tag-manager';
import { fetchGraphQl, parseQueryAST, isAbortError } from '../util/graphql';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import Body from 'components/_pages/clearance-sale';
import { clearanceSalePageProps } from 'components/_pages/clearance-sale/utils';
import { ProductListingProps } from 'components/_shared/widgets/product-listing/types';
import config from '../util/load-config';
import { AlgoliaIndexEnum } from 'types/interface';
import { prepMetaTags } from '../util/seo';
import { PageNamesEnum } from 'types/third-party/gtm';
import { logError } from '../util/error-logger';
import { cacheControl } from 'constants/cache-control';
import { logInvalidJSON } from '../util/log-invalid-json';

setGTMPageName(PageNamesEnum.clearance);

const BodyWrapper = ({
  clearanceSale,
}: {
  clearanceSale: ProductListingProps;
}) => {
  if (!clearanceSale) {
    return (
      <Container py={[3, null, 3]}>
        <QueryError />
      </Container>
    );
  }

  return <Body {...clearanceSale} />;
};

export async function getServerSideProps({
  res,
}: GetServerSidePropsContext): Promise<GetServerSidePropsResult<PageProps>> {
  try {
    const [query, operationName] = await parseQueryAST(
      clearanceProductsQuery,
      'clearanceProducts'
    );

    const { data, errors } = await fetchGraphQl<QueryClearanceProductsOutput>({
      operationName,
      query,
      logInvalidJSON,
    });

    // only fail if there is no data
    if (!data || !data.clearanceProducts) {
      // we know the clearance page should technically always have products
      // so we can assume that this is temporary
      res.statusCode = 503;
      res.setHeader('cache-control', cacheControl.temporary);

      // log error
      if (errors && errors.length > 0) {
        const [firstError] = errors;
        logError(firstError.message, 'clearance-sale.query');
      } else {
        logError('No data returned for homepage', 'clearance-sale.query');
      }

      // return empty props so we can render a failure page
      return { props: {} };
    }

    // go ahead
    res.setHeader('cache-control', cacheControl.dynamic);

    const headerTaglineTitle = config.headerTaglines['clearance-sale']?.header;
    const headerTaglineText = config.headerTaglines['clearance-sale']?.text;

    const meta = config.seo.meta.pages['clearance-sale'];

    return {
      props: {
        clearanceSale: clearanceSalePageProps(data),
        meta: { theme: 'clearancesale' },
        layout: {
          selectedTopTab: 'clearancesale',
          hideClock: true,
          hasScrollButton: true,
          searchIndexName: AlgoliaIndexEnum.Clearance,
          ...(headerTaglineTitle ? { headerTaglineTitle } : {}),
          ...(headerTaglineText ? { headerTaglineText } : {}),
          ...(meta ? { metaTags: prepMetaTags(meta) } : {}),
        },
      },
    };
  } catch (e) {
    res.statusCode = isAbortError(e) ? 503 : 500;
    res.setHeader('cache-control', cacheControl.temporary);

    logError(e, 'clearance-sale.catch');

    // return empty props so we can render a failure page
    return { props: {} };
  }
}

export default BodyWrapper;
